import { useEffect, useRef } from "react";
import "./AirCutterAnimation.css";

export default function AirCutterAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl.closest(".battle-container");
    
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions for defender
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate center of defender sprite
    const frameWidth = 192;  // Width of each frame (960px total width ÷ 5 frames)
    const frameHeight = 192; // Height of the sprite
    const centerX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const centerY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Initial attacker animation (slight scale before attack)
    attackerEl.style.transition = 'transform 0.2s ease-in-out';
    attackerEl.style.transform = 'scale(1.1)';
    
    // Create and position animation element
    const airCutter = document.createElement("div");
    airCutter.className = "air-cutter-anim";
    airCutter.style.setProperty('--sx', `${centerX - frameWidth / 2}px`);
    airCutter.style.setProperty('--sy', `${centerY - frameHeight / 2}px`);
    container.appendChild(airCutter);
    animRef.current = airCutter;
    
    // Create air blade effects at impact moment (frame 3)
    const effectTimer = setTimeout(() => {
      // Flash effect
      const flash = document.createElement('div');
      flash.className = 'air-cutter-flash';
      flash.style.left = `${centerX}px`;
      flash.style.top = `${centerY}px`;
      container.appendChild(flash);
      
      // Make defender react
      defenderEl.classList.add('air-cutter-hit');
      
      // Remove flash and reset defender
      setTimeout(() => {
        flash.remove();
        defenderEl.classList.remove('air-cutter-hit');
      }, 300);
      
      // Create wind slice particles
      for (let i = 0; i < 12; i++) {
        const particle = document.createElement('div');
        particle.className = 'air-cutter-particle';
        
        // Position around the impact point with directional bias
        const angle = (Math.random() * 160 - 80) * (Math.PI / 180); // -80 to +80 degrees
        const distance = 5 + Math.random() * 20;
        particle.style.left = `${centerX + Math.cos(angle) * distance}px`;
        particle.style.top = `${centerY + Math.sin(angle) * distance}px`;
        
        // Random rotation and scale
        particle.style.setProperty('--rotation', `${(Math.random() * 30 - 15) + 90}deg`); // Mostly horizontal
        particle.style.setProperty('--scale', `${0.7 + Math.random() * 0.6}`);
        particle.style.setProperty('--speed', `${5 + Math.random() * 8}`);
        
        container.appendChild(particle);
        
        // Auto-remove particles
        setTimeout(() => particle.remove(), 400 + Math.random() * 300);
      }
      
      // Create lingering wind effect
      const windEffect = document.createElement('div');
      windEffect.className = 'air-cutter-wind';
      windEffect.style.left = `${centerX}px`;
      windEffect.style.top = `${centerY}px`;
      container.appendChild(windEffect);
      
      // Auto-remove wind effect
      setTimeout(() => windEffect.remove(), 600);
      
    }, 300); // Sync with frame 3 impact

    // Reset attacker position after animation
    const resetTimer = setTimeout(() => {
      attackerEl.style.transition = 'transform 0.2s ease-out';
      attackerEl.style.transform = '';
    }, 400);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      if (airCutter.parentNode) {
        airCutter.remove();
      }
      onComplete();
    }, 900); // Allow full 5-frame animation to complete

    // Cleanup function
    return () => {
      clearTimeout(effectTimer);
      clearTimeout(resetTimer);
      clearTimeout(completeTimer);
      
      if (attackerEl) {
        attackerEl.style.transition = '';
        attackerEl.style.transform = '';
      }
      
      if (defenderEl) {
        defenderEl.classList.remove('air-cutter-hit');
      }
      
      if (airCutter.parentNode) {
        airCutter.remove();
      }
      
      // Remove any flash, particles or wind effects
      const elements = container.querySelectorAll('.air-cutter-flash, .air-cutter-particle, .air-cutter-wind');
      elements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

AirCutterAnimation.moveType = "flying";
